/** @jsxImportSource @emotion/react */
import {
  InputAdornment,
  Button,
  MenuItem,
  Select,
  OutlinedInput,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReducerAtom } from 'jotai/utils';
import {
  commisionPaymentStatusFilterAtom,
  commissionDateRangeFilterAtom,
  commissionsSortTermAtom,
  commissionsSortTermAtomReducer,
  currentDateRangeAtom,
} from '../../state';
import { useAtom } from 'jotai';
import {
  DateRangeOptions,
  PaymentStatus,
  PayoutCommissionsSortTypes,
} from '../../typings';
import {
  commissionDateRangePickerSlotProps,
  commissionPaymentStatusOptions,
  commissionSortOptions,
  dateRangeDropdownOptions,
} from '../../constants';
import {
  DesktopDateRangePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { ReactComponent as DownloadIcon } from '../../../../../icons/svgs/download.svg';
import { getStartAndEndDate } from '../../utils/get-start-end-date';
import getPayoutLifetimeDataAPI from '../../request/get-payout-lifetime-data';
import Loader from '../../../../components/loader';
import useNotifications from '../../../../hooks/useNotifications';
import useScreenSize from '../../../../hooks/useScreenSize';
import QSTooltip from '../../../../components/tooltip';
import DownloadButton from './download-button';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const SearchAndFilters = () => {
  const [selectedSort, dispatch] = useReducerAtom(
    commissionsSortTermAtom,
    commissionsSortTermAtomReducer
  );
  const [paymentStatusFilter, setPaymentStatusFilter] = useAtom(
    commisionPaymentStatusFilterAtom
  );
  const [selectedDateRangeOption, setSelectedDateRangeOption] = useAtom(
    commissionDateRangeFilterAtom
  );
  const [currentDateRange, setCurrentDateRange] = useAtom(currentDateRangeAtom);
  const [dateRangePickerOpen, setDateRangePickerOpen] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const { isResponsiveCustom } = useScreenSize();

  const { addNotification } = useNotifications();

  const openDateRangePicker = () => {
    setDateRangePickerOpen(true);
  };

  const closeDateRangePicker = () => {
    setDateRangePickerOpen(false);
  };

  const renderDateRangePicker = () => {
    const DateRangePicker = DesktopDateRangePicker;
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangePicker<Dayjs>
          localeText={{ start: 'Start date', end: 'End date' }}
          timezone={dayjs.tz.guess()}
          closeOnSelect={false}
          value={[
            !!currentDateRange.startDate &&
            dayjs(currentDateRange.startDate).isValid()
              ? dayjs(currentDateRange.startDate)
              : null,
            !!currentDateRange.endDate &&
            dayjs(currentDateRange.endDate).isValid()
              ? dayjs(currentDateRange.endDate)
              : null,
          ]}
          onChange={([startTime, endTime]: [Dayjs | null, Dayjs | null]) => {
            setCurrentDateRange({
              startDate: startTime?.toISOString(),
              endDate: endTime?.toISOString(),
            });
          }}
          css={css`
            display: none;
          `}
          slotProps={commissionDateRangePickerSlotProps}
          open={dateRangePickerOpen}
          onOpen={openDateRangePicker}
          onClose={closeDateRangePicker}
        />
      </LocalizationProvider>
    );
  };

  useEffect(() => {
    return () => {
      dispatch({ type: PayoutCommissionsSortTypes.reset });
      setSelectedDateRangeOption(DateRangeOptions.LAST_12_MONTHS);
      setPaymentStatusFilter(undefined);
      setCurrentDateRange({
        startDate: dayjs().toISOString(),
        endDate: dayjs().toISOString(),
      });
    };
  }, [
    setSelectedDateRangeOption,
    setPaymentStatusFilter,
    setCurrentDateRange,
    dispatch,
  ]);

  useEffect(() => {
    if (
      typeof selectedDateRangeOption === 'string' &&
      selectedDateRangeOption.length > 0 &&
      selectedDateRangeOption !== DateRangeOptions.CUSTOM_DATE_RANGE
    ) {
      setCurrentDateRange(getStartAndEndDate(selectedDateRangeOption));
    }
  }, [selectedDateRangeOption]);

  const handleDownloadLifetimeData = async () => {
    setLoading(true);
    try {
      await getPayoutLifetimeDataAPI();
    } catch(e) {
      addNotification({
        message: 'An error occurred. Please try again',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Box
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
        `}
      >
        <Typography
          css={css`
            color: #2d2d2d;
            font-family: Inter;
            font-size: 24px;
            font-style: normal;
            font-weight: 600;
            line-height: 140%;
            letter-spacing: -0.24px;
          `}
        >
          Payout & Commissions
        </Typography>
        <Box css={css`
          display: flex; 
          gap: 12px;
          align-items: center; 
        `}>
        <Box
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          <Box>
            <Select
              id="payment-date-range-select"
              variant="outlined"
              size="small"
              notched={false}
              value={selectedDateRangeOption ?? ('' as DateRangeOptions)}
              onChange={(e) =>
                setSelectedDateRangeOption(e.target.value as DateRangeOptions)
              }
              css={css`
                background: #fff;
              `}
              displayEmpty
              renderValue={(value) => {
                if (!value || value === ('' as DateRangeOptions)) {
                  return 'Date range';
                } else if (value === DateRangeOptions.CUSTOM_DATE_RANGE) {
                  return `${
                    !!currentDateRange.startDate &&
                    dayjs(currentDateRange.startDate).isValid()
                      ? dayjs(currentDateRange.startDate).format('Do MMM YYYY')
                      : 'Start date'
                  } - ${
                    !!currentDateRange.endDate &&
                    dayjs(currentDateRange.endDate).isValid()
                      ? dayjs(currentDateRange.endDate).format('Do MMM YYYY')
                      : 'End date'
                  }`;
                } else {
                  return dateRangeDropdownOptions.find(
                    (option) => option.value === value
                  )?.name;
                }
              }}
            >
              <MenuItem value="" disabled>
                <Typography color={'#aeaeae'}>Date range</Typography>
              </MenuItem>
              {dateRangeDropdownOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  onClick={(e) => {
                    if (option.value === DateRangeOptions.CUSTOM_DATE_RANGE) {
                      openDateRangePicker();
                      if (
                        !currentDateRange.startDate ||
                        !dayjs(currentDateRange.startDate).isValid()
                      ) {
                        setCurrentDateRange((prev) => ({
                          ...prev,
                          startDate: dayjs().subtract(1, 'day').toISOString(),
                        }));
                      }
                      if (
                        !currentDateRange.endDate ||
                        !dayjs(currentDateRange.endDate).isValid()
                      ) {
                        setCurrentDateRange((prev) => ({
                          ...prev,
                          endDate: dayjs().toISOString(),
                        }));
                      }
                    }
                  }}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* <Box>
            <Select
              id="payment-status-select"
              variant="outlined"
              size="small"
              notched={false}
              value={paymentStatusFilter}
              onChange={(e) =>
                setPaymentStatusFilter(e.target.value as PaymentStatus)
              }
              css={css`
                background: #fff;
              `}
              displayEmpty
              renderValue={(value) => {
                return (
                  <Typography color={'#2d2d2d'}>
                    {value || 'Payment status'}
                  </Typography>
                );
              }}
            >
              <MenuItem value="" disabled>
                <Typography color={'#aeaeae'}>Payment status</Typography>
              </MenuItem>
              {commissionPaymentStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Box> */}
          <Box>
            <Select
              id="payment-sort-select"
              variant="outlined"
              size="small"
              notched={false}
              value={selectedSort.key}
              onChange={(e) =>
                dispatch({ type: e.target.value as PayoutCommissionsSortTypes })
              }
              css={css`
                background: #fff;
              `}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <Typography color={'#aeaeae'}>Sort by</Typography>
              </MenuItem>
              {commissionSortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Box>
          {isResponsiveCustom(1100, true) ? 
          <DownloadButton onClick={handleDownloadLifetimeData} sx={{width: '270px'}}>
            {loading ? <Loader size={'24px'}/> :
              <>
                <DownloadIcon />
                Download Lifetime Raw Data
              </>
            }
          </DownloadButton> :
          <QSTooltip title="Download Lifetime Raw Data" disableInteractive={false}>
            <span>
            <DownloadButton
              onClick={() => handleDownloadLifetimeData()}>
              {loading ? <Loader size={'20px'}/> :
                  <DownloadIcon />
              }
            </DownloadButton>
            </span>
          </QSTooltip>
          }
        
        </Box>
      </Box>
      </Box>
      {renderDateRangePicker()}
    </>
  );
};

export default SearchAndFilters;
